import { useState, useMemo, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useLeads } from '@/hooks/useLeads';
import { usePartnerCatalogue } from '@/hooks/usePartnerCatalogue';
import { useAgenda } from '@/hooks/useAgenda';
import { supabase, isSupabaseConfigured } from '@/lib/supabase';
import { mockVerticals } from '@/mocks/verticals';
import { mockOffers } from '@/mocks/offers';
import {
  ArrowLeft, ArrowUpRight, Mail, Phone, MapPin, AlertTriangle, Flag, Save,
  CheckCircle, Euro, CalendarPlus, X, Send, ChevronRight, MessageCircle,
} from 'lucide-react';
import BaseSelect from '@/components/base/BaseSelect';

const STAGE_LABELS: Record<string, string> = {
  new:'Nieuw', contacted:'Gecontacteerd', showroom_planned:'Showroom gepland',
  showroom_visited:'Showroom bezocht', offerte_verstuurd:'Offerte verstuurd',
  offerte_herzien:'Offerte herzien', akkoord:'Akkoord', getekend:'Getekend',
  aanbetaling_gevraagd:'Aanbetaling gevraagd', aanbetaling_ontvangen:'Aanbetaling ontvangen',
  werkbezoek_gepland:'Werkbezoek gepland', werkbezoek_gedaan:'Werkbezoek gedaan',
  materiaal_besteld:'Materiaal besteld', levering_gepland:'Levering gepland',
  geleverd:'Geleverd', oplevering_verstuurd:'Oplevering verstuurd', afgerond:'Afgerond',
  nabetaling_gevraagd:'Nabetaling gevraagd', nabetaling_ontvangen:'Nabetaling ontvangen', verloren:'Verloren',
};

const STAGE_PROGRESS: Record<string, number> = {
  new:5, contacted:10, showroom_planned:15, showroom_visited:20,
  offerte_verstuurd:28, offerte_herzien:33, akkoord:38, getekend:45,
  aanbetaling_gevraagd:50, aanbetaling_ontvangen:55, werkbezoek_gepland:62,
  werkbezoek_gedaan:68, materiaal_besteld:74, levering_gepland:80,
  geleverd:87, oplevering_verstuurd:90, afgerond:94, nabetaling_gevraagd:97,
  nabetaling_ontvangen:100, verloren:0,
};

const TRAJECT_STAGES = [
  { key: 'new',               label: 'Intake' },
  { key: 'contacted',         label: 'Gecontacteerd' },
  { key: 'offerte_verstuurd', label: 'Prijsindicatie verstuurd' },
  { key: 'akkoord',           label: 'Showroom / akkoord' },
  { key: 'getekend',          label: 'Definitieve offerte + aanbetaling' },
  { key: 'afgerond',          label: 'Installatie + oplevering' },
];


export default function PartnerLeadDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { effectivePartnerId, user } = useAuth();

  const { leads: liveLeads, loading: leadsLoading } = useLeads(effectivePartnerId);
  const lead = useMemo(() => liveLeads.find(l => l.id === id), [id, liveLeads]);
  const vertical = useMemo(() => mockVerticals.find(v => v.id === lead?.vertical_id), [lead]);

  const { products: catalogueProducts, extras: catalogueExtras } = usePartnerCatalogue(effectivePartnerId);

  // Lead status
  const [currentStatus, setCurrentStatus] = useState(lead?.status || 'new');
  const [statusSaved, setStatusSaved] = useState(false);
  const [partnerNotes, setPartnerNotes] = useState('');
  const [notesSaved, setNotesSaved] = useState(false);

  // Quotation builder
  const [quoteProductId, setQuoteProductId] = useState<string | null>(null);
  const [quotePricePerM2, setQuotePricePerM2] = useState('');
  const [quoteSurfaceM2, setQuoteSurfaceM2] = useState('');
  const [quoteExtras, setQuoteExtras] = useState<Record<string, { enabled: boolean; price: string }>>({});
  const [quoteFreeItem, setQuoteFreeItem] = useState({ label: '', price: '' });
  const [selectedBrochures, setSelectedBrochures] = useState<string[]>([]);
  const [showEmailPreview, setShowEmailPreview] = useState(false);
  const [quoteSending, setQuoteSending] = useState(false);
  const [quoteSent, setQuoteSent] = useState(false);
  const [quoteNote, setQuoteNote] = useState('');
  const [quoteSentError, setQuoteSentError] = useState<string | null>(null);
  const [emailLanguage, setEmailLanguage] = useState<'nl' | 'en'>('nl');
  const [companyName, setCompanyName] = useState('PVC Vloer Nederland');

  // Sender profile
  const [senderName, setSenderName] = useState('');
  const [senderTitle, setSenderTitle] = useState('');
  const [senderPhone, setSenderPhone] = useState('');
  const [senderSignature, setSenderSignature] = useState('');

  // Agenda
  const { items: agendaItems, addItem: addAgendaItem } = useAgenda(effectivePartnerId, lead?.id ?? null);
  const [showAgendaModal, setShowAgendaModal] = useState(false);
  const [agendaForm, setAgendaForm] = useState({ name: '', date: '', time: '', type: 'showroom', notes: '' });

  // Dispute modal
  const [showDisputeModal, setShowDisputeModal] = useState(false);
  const [disputeReason, setDisputeReason] = useState('lead_invalid');
  const [disputeDetail, setDisputeDetail] = useState('');
  const [disputeSubmitting, setDisputeSubmitting] = useState(false);
  const [disputeSubmitted, setDisputeSubmitted] = useState(false);

  // URL sharing modals
  const [showSigningModal, setShowSigningModal] = useState(false);
  const [showDeliveryModal, setShowDeliveryModal] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentType, setPaymentType] = useState<'aanbetaling' | 'nabetaling'>('aanbetaling');
  const [copiedUrl, setCopiedUrl] = useState<string | null>(null);
  const [dynamicSigningUrl, setDynamicSigningUrl] = useState<string | null>(null);
  const [generatingSigningLink, setGeneratingSigningLink] = useState(false);

  // Fetch sender profile + company name
  useEffect(() => {
    if (!isSupabaseConfigured || !user?.id) return;
    supabase
      .from('profiles')
      .select('sender_name, sender_title, sender_phone, sender_signature')
      .eq('id', user.id)
      .single()
      .then(({ data }) => {
        if (!data) return;
        setSenderName(data.sender_name || '');
        setSenderTitle(data.sender_title || '');
        setSenderPhone(data.sender_phone || '');
        setSenderSignature(data.sender_signature || '');
      });
    if (effectivePartnerId) {
      supabase
        .from('semmy_operator_context')
        .select('company_name')
        .eq('partner_id', effectivePartnerId)
        .maybeSingle()
        .then(({ data }) => { if (data?.company_name) setCompanyName(data.company_name); });
    }
  }, [user?.id, effectivePartnerId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Pre-fill surface area from intake data
  useEffect(() => {
    const area = (lead?.intake_data as any)?.surface_area_m2;
    if (area) setQuoteSurfaceM2(String(area));
  }, [lead?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Pre-fill agenda customer name from lead
  useEffect(() => {
    if (lead) setAgendaForm(f => ({ ...f, name: `${lead.firstname} ${lead.lastname}` }));
  }, [lead?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  if (leadsLoading) {
    return (
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '64px 0'
      }}>
        <div style={{
          width: 24, height: 24, borderRadius: '50%',
          border: '2px solid var(--border-primary)',
          borderTopColor: 'var(--accent)',
          animation: 'spin 0.7s linear infinite',
        }} />
      </div>
    );
  }

  if (!lead) {
    return (
      <div className="space-y-4">
        <button
          onClick={() => navigate('/partner/leads')}
          className="flex items-center gap-2 text-sm"
          style={{ color: 'var(--text-muted)' }}
        >
          <ArrowLeft size={16} /> Terug naar leads
        </button>
        <div
          className="rounded-xl p-8 text-center border"
          style={{ background: 'var(--bg-surface-raised)', borderColor: 'var(--border-primary)' }}
        >
          <p style={{ color: 'var(--text-secondary)' }}>Lead niet gevonden</p>
          <p className="text-xs mt-2" style={{ color: 'var(--text-muted)' }}>
            Probeer terug te gaan en opnieuw te klikken.
          </p>
        </div>
      </div>
    );
  }

  const intake = lead.intake_data as Record<string, unknown>;
  const progress = STAGE_PROGRESS[currentStatus] ?? 5;

  const trackerUrl  = `${window.location.origin}/project/${lead.lead_token ?? lead.id}`;
  const signingUrl  = `${window.location.origin}/offerte/${lead.lead_token ?? lead.id}`;
  const deliveryUrl = `${window.location.origin}/oplevering/${lead.lead_token ?? lead.id}`;
  const paymentUrl  = `${window.location.origin}/betaling/${lead.lead_token ?? lead.id}`;

  const copyAndSet = (url: string) => {
    navigator.clipboard.writeText(url);
    setCopiedUrl(url);
    setTimeout(() => setCopiedUrl(null), 2000);
  };

  const waLink = (url: string, msg: string) =>
    `https://wa.me/${(lead.phone || '').replace(/\D/g, '')}?text=${encodeURIComponent(msg)}`;

  const advanceStage = async (newStage: string) => {
    setCurrentStatus(newStage);
    setStatusSaved(true);
    setTimeout(() => setStatusSaved(false), 2000);
    if (isSupabaseConfigured) {
      await supabase.from('leads').update({ status: newStage }).eq('id', lead.id);
    }
  };

  type Action = { label: string; description?: string; primary?: boolean; action: () => void };
  const getFunnelActions = (): Action[] => {
    const s = currentStatus;
    if (s === 'new') return [{ label: 'Markeer als gecontacteerd', primary: true, description: 'Bevestig dat je de klant hebt gebeld', action: () => advanceStage('contacted') }];
    if (s === 'contacted') return [
      { label: 'Plan showroom bezoek', primary: true, description: 'Nodig de klant uit in de showroom', action: () => advanceStage('showroom_planned') },
      { label: 'Sla showroom over → stuur offerte', action: () => { advanceStage('offerte_verstuurd'); setShowSigningModal(true); } },
    ];
    if (s === 'showroom_planned') return [{ label: 'Showroom bezocht ✓', primary: true, action: () => advanceStage('showroom_visited') }];
    if (s === 'showroom_visited') return [{ label: 'Stuur offerte link', primary: true, description: 'Klant ontvangt link om offerte te bekijken en te ondertekenen', action: () => { advanceStage('offerte_verstuurd'); setShowSigningModal(true); } }];
    if (s === 'offerte_verstuurd') return [
      { label: 'Klant gaat akkoord', primary: true, action: () => advanceStage('akkoord') },
      { label: 'Herziene offerte sturen', description: 'Klant wil aanpassingen', action: () => { advanceStage('offerte_herzien'); setShowSigningModal(true); } },
    ];
    if (s === 'offerte_herzien') return [{ label: 'Klant gaat akkoord', primary: true, action: () => advanceStage('akkoord') }];
    if (s === 'akkoord') return [{ label: 'Stuur contract ter ondertekening', primary: true, action: () => { advanceStage('getekend'); setShowSigningModal(true); } }];
    if (s === 'getekend') return [{ label: 'Vraag aanbetaling (50%)', primary: true, action: () => { advanceStage('aanbetaling_gevraagd'); setPaymentType('aanbetaling'); setShowPaymentModal(true); } }];
    if (s === 'aanbetaling_gevraagd') return [{ label: 'Aanbetaling ontvangen ✓', primary: true, action: () => advanceStage('aanbetaling_ontvangen') }];
    if (s === 'aanbetaling_ontvangen') return [{ label: 'Plan werkbezoek (opmeting)', primary: true, action: () => advanceStage('werkbezoek_gepland') }];
    if (s === 'werkbezoek_gepland') return [{ label: 'Werkbezoek afgerond ✓', primary: true, action: () => advanceStage('werkbezoek_gedaan') }];
    if (s === 'werkbezoek_gedaan') return [{ label: 'Materiaal besteld ✓', primary: true, action: () => advanceStage('materiaal_besteld') }];
    if (s === 'materiaal_besteld') return [{ label: 'Plan levering & installatie', primary: true, action: () => advanceStage('levering_gepland') }];
    if (s === 'levering_gepland') return [{ label: 'Vloer geleverd en gelegd ✓', primary: true, action: () => advanceStage('geleverd') }];
    if (s === 'geleverd') return [{ label: 'Stuur opleveringsformulier', primary: true, action: () => { advanceStage('oplevering_verstuurd'); setShowDeliveryModal(true); } }];
    if (s === 'oplevering_verstuurd') return [{ label: 'Oplevering bevestigd ✓', primary: true, action: () => advanceStage('afgerond') }];
    if (s === 'afgerond') return [{ label: 'Vraag nabetaling (resterende 50%)', primary: true, action: () => { advanceStage('nabetaling_gevraagd'); setPaymentType('nabetaling'); setShowPaymentModal(true); } }];
    if (s === 'nabetaling_gevraagd') return [{ label: 'Nabetaling ontvangen ✓, project volledig afgerond!', primary: true, action: () => advanceStage('nabetaling_ontvangen') }];
    return [];
  };

  // ── Quote calculations ──
  const activeProducts = catalogueProducts.filter(p => p.active);
  const activeExtras = catalogueExtras.filter(e => e.active);
  const surfaceM2 = parseFloat(quoteSurfaceM2) || 0;
  const pricePerM2 = parseFloat(quotePricePerM2) || 0;
  const allInLine = surfaceM2 * pricePerM2;
  const extrasLines = activeExtras
    .filter(e => quoteExtras[e.id]?.enabled)
    .map(e => {
      const price = parseFloat(quoteExtras[e.id]?.price || String(e.price)) || 0;
      return { name: e.name, amount: e.unit === 'm2' ? price * surfaceM2 : price };
    });
  const freeLineAmount = parseFloat(quoteFreeItem.price) || 0;
  const subtotal = allInLine + extrasLines.reduce((s, l) => s + l.amount, 0) + (quoteFreeItem.label ? freeLineAmount : 0);
  const btw = subtotal * 0.21;
  const total = subtotal + btw;

  const handleProductSelect = (productId: string) => {
    if (quoteProductId === productId) {
      setQuoteProductId(null);
      setQuotePricePerM2('');
    } else {
      setQuoteProductId(productId);
      const p = catalogueProducts.find(x => x.id === productId);
      if (p && p.price_per_m2 > 0) setQuotePricePerM2(String(p.price_per_m2));
    }
  };

  const setExtraEnabled = (id: string, enabled: boolean) =>
    setQuoteExtras(prev => ({ ...prev, [id]: { ...(prev[id] ?? { enabled: false, price: String(activeExtras.find(e => e.id === id)?.price ?? 0) }), enabled } }));

  const setExtraPrice = (id: string, price: string) =>
    setQuoteExtras(prev => ({ ...prev, [id]: { ...(prev[id] ?? { enabled: false, price }), price } }));

  const toggleBrochure = (name: string) =>
    setSelectedBrochures(prev => prev.includes(name) ? prev.filter(b => b !== name) : [...prev, name]);

  const selectedProduct = catalogueProducts.find(p => p.id === quoteProductId);
  const allBrochureNames = activeProducts.map(p => p.name);
  const brochureList = selectedBrochures.length === allBrochureNames.length && allBrochureNames.length > 0 ? 'Alle brochures' : selectedBrochures.length > 0 ? selectedBrochures.join(', ') : 'Geen brochure';



  const handleSendEmail = async () => {
    setQuoteSending(true);
    setQuoteSentError(null);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;
      const allLineItems = [
        ...(selectedProduct && surfaceM2 > 0 && pricePerM2 > 0 ? [{
          name: `${selectedProduct.name} · ${surfaceM2} m² × €${pricePerM2.toFixed(2)}/m²`,
          amount: surfaceM2 * pricePerM2,
        }] : []),
        ...extrasLines,
        ...(quoteFreeItem.label && freeLineAmount > 0 ? [{ name: quoteFreeItem.label, amount: freeLineAmount }] : []),
      ];
      const body = {
        lead_id: lead.id,
        partner_id: effectivePartnerId,
        line_items: allLineItems,
        subtotal, btw, total,
        note: quoteNote || undefined,
        brochures: selectedBrochures,
        language: 'nl',
        sender_name: senderName || undefined,
        sender_title: senderTitle || undefined,
        sender_phone: senderPhone || undefined,
        sender_signature: senderSignature || undefined,
      };
      const supabaseUrl = import.meta.env.VITE_PUBLIC_SUPABASE_URL as string;
      const res = await fetch(`${supabaseUrl}/functions/v1/send_quotation`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify(body),
      });
      const json = await res.json();
      if (!res.ok || json.error) {
        setQuoteSentError(json.error || 'Onbekende fout');
      } else {
        setQuoteSent(true);
        setShowEmailPreview(false);
        setCurrentStatus('offerte_verstuurd');
        setTimeout(() => setQuoteSent(false), 4000);
      }
    } catch (err) {
      setQuoteSentError(String(err));
    } finally {
      setQuoteSending(false);
    }
  };

  const handleGenerateSigningLink = async () => {
    setGeneratingSigningLink(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;
      const supabaseUrl = import.meta.env.VITE_PUBLIC_SUPABASE_URL as string;
      const res = await fetch(`${supabaseUrl}/functions/v1/sign_quote`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify({ action: 'generate_link', lead_id: lead.id }),
      });
      const json = await res.json();
      if (json.token) {
        setDynamicSigningUrl(`${window.location.origin}/offerte/${json.token}`);
        setShowSigningModal(true);
      }
    } catch (err) {
      console.error('generate_link failed:', err);
    } finally {
      setGeneratingSigningLink(false);
    }
  };

  const trajectIdx = TRAJECT_STAGES.findIndex(s => s.key === currentStatus);
  const effectiveIdx = trajectIdx >= 0 ? trajectIdx : 0;

  // URL share modal
  const UrlShareModal = ({ type, url, onClose }: { type: 'signing' | 'delivery' | 'payment'; url: string; onClose: () => void }) => {
    const title = type === 'signing' ? 'Offertelink' : type === 'delivery' ? 'Opleveringsformulier' : `${paymentType === 'aanbetaling' ? 'Aanbetaling' : 'Nabetaling'} betaallink`;
    const waMsg = type === 'signing'
      ? `Goedendag ${lead.firstname}, uw offerte staat klaar om te bekijken en te ondertekenen: ${url}`
      : type === 'delivery'
      ? `Goedendag ${lead.firstname}, uw vloer is gelegd. Bevestig hier de oplevering: ${url}`
      : `Goedendag ${lead.firstname}, hier is uw betalingslink (${paymentType}): ${url}`;
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.50)', backdropFilter: 'blur(8px)' }}>
        <div className="rounded-xl p-6 w-full max-w-md" style={{ background: 'var(--bg-modal)', border: '1px solid var(--border-modal)', boxShadow: 'var(--shadow-card)' }}>
          <h3 className="text-sm font-semibold mb-2" style={{ color: 'var(--text-primary)' }}>{title}</h3>
          <p className="text-xs mb-4" style={{ color: 'var(--text-secondary)' }}>
            Stuur deze link naar <strong>{lead.firstname} {lead.lastname}</strong> via WhatsApp of e-mail.
          </p>
          <div className="rounded-lg px-3 py-2.5 text-xs font-mono break-all mb-4" style={{ background: 'var(--bg-input)', color: 'var(--text-secondary)', border: '1px solid var(--border-primary)' }}>
            {url}
          </div>
          <div className="flex gap-2">
            <button onClick={() => copyAndSet(url)} className="flex-1 py-2 text-xs font-medium rounded-lg transition-all" style={{ background: copiedUrl === url ? 'var(--success-bg)' : 'var(--accent)', color: copiedUrl === url ? 'var(--success-text)' : '#fff' }}>
              {copiedUrl === url ? '✓ Gekopieerd' : 'Kopieer link'}
            </button>
            <a href={waLink(url, waMsg)} target="_blank" rel="noopener noreferrer" className="flex-1 py-2 text-xs font-medium text-center rounded-lg flex items-center justify-center gap-1.5" style={{ background: 'var(--wa-bg)', color: 'var(--wa-text)', border: '0.5px solid var(--wa-border)' }}>
              <MessageCircle size={12} /> WhatsApp
            </a>
            <button onClick={onClose} className="px-3 text-xs rounded-lg" style={{ color: 'var(--text-muted)', border: '1px solid var(--border-primary)' }}>Sluiten</button>
          </div>
        </div>
      </div>
    );
  };

  const cardStyle = { background: 'var(--bg-surface-raised)', borderColor: 'var(--border-primary)' } as const;
  const labelStyle = { color: 'var(--text-muted)', fontSize: '11px', fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase' as const };
  const inputStyle = { background: 'var(--bg-input)', border: '1px solid var(--border-primary)', color: 'var(--text-primary)' } as const;

  return (
    <div className="space-y-4 max-w-[1400px]">
      {/* Back + header */}
      <div className="flex items-center gap-3">
        <button onClick={() => navigate('/partner/leads')} className="p-1.5 rounded-lg transition-colors hover:bg-[var(--bg-surface-hover)]" style={{ color: 'var(--text-muted)' }}
        >
          <ArrowLeft size={18} />
        </button>
        <div>
          <h1 className="text-xl font-semibold tracking-tight" style={{ color: 'var(--text-primary)' }}>{lead.firstname} {lead.lastname}</h1>
          <p className="text-sm" style={{ color: 'var(--text-tertiary)' }}>{lead.city} · {lead.vertical_id}</p>
        </div>
        <div className="ml-auto flex items-center gap-2">
          {quoteSent && (
            <span className="flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-lg" style={{ background: 'var(--success-bg)', color: 'var(--success-text)' }}>
              <CheckCircle size={12} /> E-mail verzonden
            </span>
          )}
          <button onClick={() => setShowDisputeModal(true)} className="flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-lg border transition-all" style={{ color: 'var(--danger-text)', borderColor: 'var(--danger-border)', background: 'var(--danger-bg)' }}>
            <AlertTriangle size={12} /> Melding maken
          </button>
        </div>
      </div>

      {/* ── Top grid: contact + intake ── */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Contact */}
        <div className="rounded-xl p-5 border" style={cardStyle}>
          <h3 className="text-[11px] font-semibold uppercase tracking-wider mb-4" style={{ color: 'var(--text-muted)' }}>Contact</h3>
          <div className="grid grid-cols-2 gap-3 text-sm">
            {[
              { label: 'E-mail', value: lead.email, href: `mailto:${lead.email}` },
              { label: 'Telefoon', value: lead.phone, href: `tel:${lead.phone}` },
              { label: 'Postcode', value: lead.postcode },
              { label: 'Stad', value: lead.city },
            ].map(item => (
              <div key={item.label}>
                <p className="text-[11px] uppercase tracking-wider mb-1" style={{ color: 'var(--text-muted)' }}>{item.label}</p>
                {item.href
                  ? <a href={item.href} className="transition-colors" style={{ color: 'var(--accent)' }}>{item.value || ''}</a>
                  : <p style={{ color: 'var(--text-secondary)' }}>{item.value || ''}</p>}
              </div>
            ))}
          </div>
          <div className="flex gap-2 mt-4 pt-4" style={{ borderTop: '1px solid var(--border-separator)' }}>
            <a href={`tel:${lead.phone}`} className="flex-1 py-2 text-xs font-medium text-center rounded-lg flex items-center justify-center gap-1" style={{ background: 'var(--bg-input)', color: 'var(--text-secondary)', border: '1px solid var(--border-primary)' }}>
              <Phone size={12} /> Bellen
            </a>
            <a href={`https://wa.me/${(lead.phone || '').replace(/\D/g, '')}`} target="_blank" rel="noopener noreferrer" className="flex-1 py-2 text-xs font-medium text-center rounded-lg flex items-center justify-center gap-1.5" style={{ background: 'var(--wa-bg)', color: 'var(--wa-text)', border: '0.5px solid var(--wa-border)' }}>
              <MessageCircle size={12} /> WhatsApp
            </a>
            <a href={`mailto:${lead.email}`} className="flex-1 py-2 text-xs font-medium text-center rounded-lg flex items-center justify-center gap-1" style={{ background: 'var(--bg-input)', color: 'var(--text-secondary)', border: '1px solid var(--border-primary)' }}>
              <Mail size={12} /> E-mail
            </a>
          </div>
          <div className="flex gap-2 mt-2">
            <button
              onClick={() => copyAndSet(trackerUrl)}
              className="flex-1 py-2 text-xs font-medium rounded-lg flex items-center justify-center gap-1.5 transition-all"
              style={copiedUrl === trackerUrl
                ? { background: 'var(--success-bg)', color: 'var(--success-text)', border: '1px solid var(--success-border)' }
                : { background: 'var(--bg-input)', color: 'var(--text-secondary)', border: '1px solid var(--border-primary)' }}
            >
              <ArrowUpRight size={12} />
              {copiedUrl === trackerUrl ? '✓ Gekopieerd' : 'Projectpagina kopiëren'}
            </button>
            <a href={trackerUrl} target="_blank" rel="noopener noreferrer" className="px-3 py-2 text-xs rounded-lg flex items-center justify-center" style={{ background: 'var(--bg-input)', color: 'var(--text-muted)', border: '1px solid var(--border-primary)' }}>
              Bekijken
            </a>
          </div>
        </div>

        {/* Project details */}
        <div className="rounded-xl p-5 border" style={cardStyle}>
          <h3 className="text-[11px] font-semibold uppercase tracking-wider mb-4" style={{ color: 'var(--text-muted)' }}>Project details</h3>
          <div className="grid grid-cols-2 gap-3 text-sm">
            {intake.surface_area_m2 && <div><p style={labelStyle} className="mb-1">Oppervlakte</p><p className="font-medium" style={{ color: 'var(--text-secondary)' }}>{String(intake.surface_area_m2)} m²</p></div>}
            {intake.floor_type && <div><p style={labelStyle} className="mb-1">Vloertype</p><p className="font-medium capitalize" style={{ color: 'var(--text-secondary)' }}>{String(intake.floor_type)}</p></div>}
            {intake.floor_subtype && <div><p style={labelStyle} className="mb-1">Subtype</p><p className="font-medium" style={{ color: 'var(--text-secondary)' }}>{String(intake.floor_subtype)}</p></div>}
            <div><p style={labelStyle} className="mb-1">Vloerverwarming</p>
              <div className="flex items-center gap-2">
                {intake.has_underfloor_heating && <i className="ti ti-flame" style={{ color: '#BA7517', fontSize: '14px' }} />}
                <p className="font-medium" style={{ color: intake.has_underfloor_heating ? '#BA7517' : 'var(--text-secondary)' }}>
                  {intake.has_underfloor_heating ? 'Aanwezig' : 'Niet aanwezig'}
                </p>
              </div>
            </div>
            {intake.property_type && <div><p style={labelStyle} className="mb-1">Woning</p><p className="font-medium capitalize" style={{ color: 'var(--text-secondary)' }}>{String(intake.property_type).replace(/_/g, ' ')}</p></div>}
          </div>
        </div>

      </div>

      {/* ── Quotation panel (12-col bento) ── */}
      <div className="grid grid-cols-12 gap-4">

        {/* LEFT 5 cols — builder */}
        <div className="col-span-12 md:col-span-7 lg:col-span-5 rounded-xl p-5 border space-y-5" style={cardStyle}>
          <h3 className="text-[11px] font-semibold uppercase tracking-wider" style={{ color: 'var(--text-muted)' }}>Offerte opstellen</h3>

          {/* Product chips */}
          <div>
            <p style={labelStyle} className="mb-2">Product</p>
            <div className="flex gap-2 flex-wrap">
              {activeProducts.map(p => (
                <button key={p.id} onClick={() => handleProductSelect(p.id)} className="px-2.5 py-1 rounded-lg text-xs border transition-all"
                  style={quoteProductId === p.id
                    ? { background: 'var(--accent-dim)', color: 'var(--accent)', borderColor: 'var(--accent)' }
                    : { background: 'var(--bg-input)', color: 'var(--text-secondary)', borderColor: 'var(--border-primary)' }}
                >
                  {p.name}
                </button>
              ))}
              {activeProducts.length === 0 && (
                <span className="text-xs" style={{ color: 'var(--text-muted)' }}>Geen producten — open Catalogus om producten toe te voegen</span>
              )}
            </div>
          </div>

          {/* m² + price per m² */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label style={labelStyle} className="block mb-1.5">Oppervlakte</label>
              <div className="relative">
                <input type="number" min="0" value={quoteSurfaceM2} onChange={e => setQuoteSurfaceM2(e.target.value)} placeholder="0"
                  className="w-full px-3 py-2 text-sm rounded-lg outline-none pr-8" style={inputStyle} />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs pointer-events-none" style={{ color: 'var(--text-muted)' }}>m²</span>
              </div>
            </div>
            <div>
              <label style={labelStyle} className="block mb-1.5">All-in prijs</label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-xs pointer-events-none" style={{ color: 'var(--text-muted)' }}>€</span>
                <input type="number" min="0" step="0.01" value={quotePricePerM2} onChange={e => setQuotePricePerM2(e.target.value)} placeholder="0,00"
                  className="w-full pl-6 pr-3 py-2 text-sm rounded-lg outline-none" style={inputStyle} />
              </div>
              <p className="text-[11px] mt-1" style={{ color: 'var(--text-muted)' }}>incl. installatie /m²</p>
            </div>
          </div>

          {/* Extras */}
          {activeExtras.length > 0 && (
            <div>
              <p style={labelStyle} className="mb-2">Extra's</p>
              <div className="space-y-1.5">
                {activeExtras.map(extra => {
                  const state = quoteExtras[extra.id] ?? { enabled: false, price: String(extra.price) };
                  return (
                    <div key={extra.id} className="flex items-center gap-2 px-3 py-2 rounded-lg transition-colors"
                      style={{ background: state.enabled ? 'var(--accent-dim)' : 'var(--bg-input)', border: `1px solid ${state.enabled ? 'var(--accent)' : 'var(--border-separator)'}` }}
                    >
                      <button onClick={() => setExtraEnabled(extra.id, !state.enabled)}
                        className="w-4 h-4 rounded flex-shrink-0 flex items-center justify-center transition-all"
                        style={{ background: state.enabled ? 'var(--accent)' : 'transparent', border: `1px solid ${state.enabled ? 'var(--accent)' : 'var(--border-primary)'}` }}
                      >
                        {state.enabled && <span style={{ color: '#fff', fontSize: '9px', lineHeight: 1 }}>✓</span>}
                      </button>
                      <span className="flex-1 text-xs" style={{ color: state.enabled ? 'var(--accent)' : 'var(--text-secondary)' }}>{extra.name}</span>
                      <div className="relative w-20">
                        <span className="absolute left-1.5 top-1/2 -translate-y-1/2 text-[11px] pointer-events-none" style={{ color: 'var(--text-muted)' }}>€</span>
                        <input type="number" min="0" step="0.01" value={state.price}
                          onChange={e => setExtraPrice(extra.id, e.target.value)}
                          className="w-full pl-4 pr-1 py-0.5 text-xs rounded outline-none"
                          style={{ background: 'var(--bg-surface-raised)', border: '1px solid var(--border-separator)', color: 'var(--text-secondary)' }}
                        />
                      </div>
                      <span className="text-[11px] w-8 text-right shrink-0" style={{ color: 'var(--text-muted)' }}>{extra.unit === 'm2' ? '/m²' : 'vast'}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Free text extra */}
          <div>
            <p style={labelStyle} className="mb-2">Vrije extra</p>
            <div className="flex gap-2">
              <input type="text" value={quoteFreeItem.label} onChange={e => setQuoteFreeItem(f => ({ ...f, label: e.target.value }))} placeholder="Omschrijving..." className="flex-1 px-3 py-2 text-xs rounded-lg outline-none" style={inputStyle} />
              <div className="relative w-24">
                <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-xs pointer-events-none" style={{ color: 'var(--text-muted)' }}>€</span>
                <input type="number" min="0" step="0.01" value={quoteFreeItem.price} onChange={e => setQuoteFreeItem(f => ({ ...f, price: e.target.value }))} placeholder="0,00" className="w-full pl-5 pr-2 py-2 text-xs rounded-lg outline-none" style={inputStyle} />
              </div>
            </div>
          </div>

          {/* Personal note */}
          <div>
            <p style={labelStyle} className="mb-2">Persoonlijke noot</p>
            <textarea value={partnerNotes} onChange={e => setPartnerNotes(e.target.value)} rows={3} placeholder="Aanvullende opmerkingen voor de klant..." className="w-full text-xs rounded-lg px-3 py-2 outline-none resize-none" style={inputStyle} />
            <div className="flex justify-end mt-2">
              <button onClick={() => { setNotesSaved(true); setTimeout(() => setNotesSaved(false), 2000); }} className="flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-lg transition-all" style={{ background: notesSaved ? 'var(--success-bg)' : 'var(--bg-input)', color: notesSaved ? 'var(--success-text)' : 'var(--text-secondary)', border: '1px solid var(--border-primary)' }}>
                {notesSaved ? <><CheckCircle size={11} /> Opgeslagen</> : <><Save size={11} /> Opslaan</>}
              </button>
            </div>
          </div>
        </div>

        {/* MIDDLE 4 cols — live calculation + email */}
        <div className="col-span-12 md:col-span-5 lg:col-span-4 space-y-4">

          {/* Live totaal */}
          <div className="rounded-xl p-5 border" style={cardStyle}>
            <h3 className="text-[11px] font-semibold uppercase tracking-wider mb-4" style={{ color: 'var(--text-muted)' }}>Live berekening</h3>
            <div className="space-y-1.5">
              {allInLine > 0 && (
                <div className="flex justify-between text-xs">
                  <span style={{ color: 'var(--text-secondary)' }}>All-in ({surfaceM2}m² × €{pricePerM2}/m²)</span>
                  <span style={{ color: 'var(--text-primary)' }}>€{allInLine.toFixed(2)}</span>
                </div>
              )}
              {extrasLines.map(l => (
                <div key={l.name} className="flex justify-between text-xs">
                  <span style={{ color: 'var(--text-secondary)' }}>{l.name}</span>
                  <span style={{ color: 'var(--text-primary)' }}>€{l.amount.toFixed(2)}</span>
                </div>
              ))}
              {quoteFreeItem.label && freeLineAmount > 0 && (
                <div className="flex justify-between text-xs">
                  <span style={{ color: 'var(--text-secondary)' }}>{quoteFreeItem.label}</span>
                  <span style={{ color: 'var(--text-primary)' }}>€{freeLineAmount.toFixed(2)}</span>
                </div>
              )}
              {subtotal > 0 && (
                <>
                  <div className="pt-2 mt-2" style={{ borderTop: '1px solid var(--border-separator)' }}>
                    <div className="flex justify-between text-xs">
                      <span style={{ color: 'var(--text-secondary)' }}>Subtotaal</span>
                      <span style={{ color: 'var(--text-secondary)' }}>€{subtotal.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between text-xs mt-1">
                      <span style={{ color: 'var(--text-muted)' }}>BTW (21%)</span>
                      <span style={{ color: 'var(--text-muted)' }}>€{btw.toFixed(2)}</span>
                    </div>
                  </div>
                  <div className="flex justify-between items-baseline pt-2" style={{ borderTop: '1px solid var(--border-separator)' }}>
                    <span className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>Totaal incl. BTW</span>
                    <span className="text-xl font-bold" style={{ color: 'var(--accent)' }}>€{total.toFixed(2)}</span>
                  </div>
                </>
              )}
              {subtotal === 0 && (
                <p className="text-xs py-2 text-center" style={{ color: 'var(--text-muted)' }}>Selecteer een product en voer oppervlakte in</p>
              )}
            </div>
          </div>

          {/* Brochures */}
          <div className="rounded-xl p-5 border" style={cardStyle}>
            <h3 className="text-[11px] font-semibold uppercase tracking-wider mb-3" style={{ color: 'var(--text-muted)' }}>Brochures meesturen</h3>
            <div className="flex gap-2 flex-wrap">
              <button
                onClick={() => setSelectedBrochures(selectedBrochures.length === allBrochureNames.length ? [] : [...allBrochureNames])}
                className="px-2.5 py-1 rounded-lg text-xs border transition-all"
                style={selectedBrochures.length === allBrochureNames.length && allBrochureNames.length > 0 ? { background: 'var(--accent-dim)', color: 'var(--accent)', borderColor: 'var(--accent)' } : { background: 'var(--bg-input)', color: 'var(--text-secondary)', borderColor: 'var(--border-primary)' }}
              >
                Alle
              </button>
              {allBrochureNames.map(b => (
                <button key={b} onClick={() => toggleBrochure(b)} className="px-2.5 py-1 rounded-lg text-xs border transition-all"
                  style={selectedBrochures.includes(b) ? { background: 'var(--accent-dim)', color: 'var(--accent)', borderColor: 'var(--accent)' } : { background: 'var(--bg-input)', color: 'var(--text-secondary)', borderColor: 'var(--border-primary)' }}
                >
                  {b}
                </button>
              ))}
            </div>
          </div>

          {/* Email preview */}
          <div className="rounded-xl p-5 border" style={cardStyle}>
            <h3 className="text-[11px] font-semibold uppercase tracking-wider mb-3" style={{ color: 'var(--text-muted)' }}>E-mail versturen</h3>
            <div
              className="rounded-lg p-3 mb-3"
              style={{ background: 'var(--bg-input)', border: '1px solid var(--border-separator)' }}
            >
              <p className="text-[11px] mb-1" style={{ color: 'var(--text-muted)' }}>
                Van: <span style={{ color: 'var(--text-secondary)' }}>{companyName}</span>
              </p>
              <p className="text-[11px] mb-1" style={{ color: 'var(--text-muted)' }}>
                Aan: <span style={{ color: 'var(--text-secondary)' }}>{lead.email || '—'}</span>
              </p>
              <p className="text-[11px]" style={{ color: 'var(--text-muted)' }}>
                Onderwerp: <span style={{ color: 'var(--text-secondary)' }}>Uw offerte op maat · {companyName}</span>
              </p>
            </div>
            <textarea
              value={quoteNote}
              onChange={e => setQuoteNote(e.target.value)}
              placeholder="Persoonlijke noot voor de klant (optioneel)..."
              rows={2}
              className="w-full px-3 py-2 text-xs rounded-lg outline-none resize-none mb-3"
              style={{ background: 'var(--bg-input)', border: '1px solid var(--border-primary)', color: 'var(--text-primary)' }}
            />
            {quoteSentError && (
              <p className="text-xs mb-2 px-2 py-1.5 rounded-lg" style={{ color: 'var(--danger-text)', background: 'var(--danger-bg)', border: '1px solid var(--danger-border)' }}>
                ⚠ {quoteSentError}
              </p>
            )}
            {quoteSent && (
              <p className="text-xs mb-2 px-2 py-1.5 rounded-lg" style={{ color: 'var(--success-text)', background: 'var(--success-bg)', border: '1px solid var(--success-border)' }}>
                ✓ Offerte verstuurd naar {lead.email}
              </p>
            )}
            <div className="flex gap-2">
              <button onClick={() => setShowEmailPreview(true)} disabled={subtotal === 0}
                className="flex-1 flex items-center justify-center gap-1.5 py-2 text-xs font-medium rounded-lg transition-all disabled:opacity-40"
                style={{ background: 'var(--accent)', color: '#fff' }}
              >
                <Send size={12} /> Bevestigen & versturen
              </button>
            </div>
          </div>
        </div>

        {/* RIGHT 3 cols — agenda + traject */}
        <div className="col-span-12 lg:col-span-3 space-y-4">

          {/* Agenda */}
          <div className="rounded-xl p-5 border" style={cardStyle}>
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-[11px] font-semibold uppercase tracking-wider" style={{ color: 'var(--text-muted)' }}>Agenda</h3>
              <button onClick={() => setShowAgendaModal(true)} className="flex items-center gap-1 text-[11px] transition-colors" style={{ color: 'var(--accent)' }}>
                <CalendarPlus size={12} /> Toevoegen
              </button>
            </div>
            {(() => {
              const TYPE_LABELS: Record<string, string> = { showroom: 'Showroom bezoek', werkbezoek: 'Werkbezoek', installatie: 'Installatie', anders: 'Afspraak' };
              return (
                <div className="space-y-2">
                  {agendaItems.map(item => (
                    <div key={item.id} className="flex items-start gap-2.5 px-3 py-2.5 rounded-lg" style={{ background: 'var(--bg-input)', border: '1px solid var(--border-separator)' }}>
                      <div className="w-1.5 h-1.5 rounded-full mt-1.5 shrink-0" style={{ background: 'var(--accent)' }} />
                      <div className="min-w-0">
                        <p className="text-xs font-medium truncate" style={{ color: 'var(--text-secondary)' }}>{item.name}</p>
                        <p className="text-[11px] mt-0.5" style={{ color: 'var(--text-muted)' }}>{TYPE_LABELS[item.type] || item.type} · {item.date} {item.time}</p>
                      </div>
                    </div>
                  ))}
                  {agendaItems.length === 0 && (
                    <p className="text-[11px] text-center py-2" style={{ color: 'var(--text-muted)' }}>Geen afspraken</p>
                  )}
                </div>
              );
            })()}
          </div>

          {/* Traject */}
          <div className="rounded-xl p-5 border" style={cardStyle}>
            <h3 className="text-[11px] font-semibold uppercase tracking-wider mb-3" style={{ color: 'var(--text-muted)' }}>Traject</h3>
            <div className="space-y-1 traject-mobile">
              {TRAJECT_STAGES.map((stage, i) => {
                const isPast = i < effectiveIdx;
                const isCurrent = i === effectiveIdx;

                const handleTrajectClick = () => {
                  switch (stage.key) {
                    case 'new':
                      navigate('/partner/semmy');
                      break;
                    case 'contacted':
                      advanceStage('contacted');
                      window.open(
                        `https://wa.me/${(lead.phone || '').replace(/\D/g, '')}`,
                        '_blank'
                      );
                      break;
                    case 'offerte_verstuurd':
                      setShowEmailPreview(true);
                      break;
                    case 'akkoord':
                      setShowAgendaModal(true);
                      break;
                    case 'getekend':
                      handleGenerateSigningLink();
                      break;
                    case 'afgerond':
                      advanceStage('afgerond');
                      break;
                    default:
                      advanceStage(stage.key);
                  }
                };

                return (
                  <button
                    key={stage.key}
                    onClick={handleTrajectClick}
                    className="w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-left transition-all"
                    style={
                      isCurrent
                        ? { background: 'var(--accent-dim)', border: '1px solid var(--accent)' }
                        : isPast
                        ? { background: 'transparent', border: '1px solid transparent' }
                        : { background: 'transparent', border: '1px solid transparent' }
                    }
                  >
                    <div
                      className="w-4 h-4 rounded-full flex items-center justify-center shrink-0"
                      style={{
                        background: isPast ? 'var(--success-dot)' : isCurrent ? 'var(--accent)' : 'var(--bg-input)',
                        border: `1px solid ${isPast ? 'var(--success-dot)' : isCurrent ? 'var(--accent)' : 'var(--border-primary)'}`,
                      }}
                    >
                      {isPast && <span style={{ color: '#fff', fontSize: '8px' }}>✓</span>}
                      {isCurrent && <div className="w-1.5 h-1.5 rounded-full bg-white" />}
                    </div>
                    <span
                      className="text-xs"
                      style={{
                        color: isCurrent ? 'var(--accent)' : isPast ? 'var(--text-muted)' : 'var(--text-secondary)',
                      }}
                    >
                      {stage.label}
                    </span>
                    {isCurrent && (
                      <ChevronRight size={12} className="ml-auto shrink-0" style={{ color: 'var(--accent)' }} />
                    )}
                  </button>
                );
              })}
            </div>
            {currentStatus === 'offerte_verstuurd' && (
              <div className="mt-3 pt-3" style={{ borderTop: '1px solid var(--border-separator)' }}>
                <button
                  onClick={handleGenerateSigningLink}
                  disabled={generatingSigningLink}
                  className="w-full flex items-center justify-center gap-1.5 py-2 text-xs font-medium rounded-lg transition-all disabled:opacity-60"
                  style={{ background: 'var(--accent)', color: '#fff' }}
                >
                  {generatingSigningLink
                    ? 'Laden...'
                    : <><Send size={11} /> Verstuur ondertekeningslink</>}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ── Dispute modal ── */}
      {showDisputeModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.50)', backdropFilter: 'blur(8px)' }}>
          <div className="rounded-xl p-6 w-full max-w-md" style={{ background: 'var(--bg-modal)', border: '1px solid var(--border-modal)', boxShadow: 'var(--shadow-card)' }}>
            <h3 className="text-sm font-semibold mb-4" style={{ color: 'var(--text-primary)' }}>Melding maken over deze lead</h3>
            {disputeSubmitted ? (
              <div className="text-center py-4">
                <CheckCircle size={32} className="mx-auto mb-3" style={{ color: 'var(--success-dot)' }} />
                <p className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>Melding ingediend</p>
                <p className="text-xs mt-1" style={{ color: 'var(--text-secondary)' }}>Ons team behandelt dit binnen 2 werkdagen.</p>
                <button onClick={() => { setShowDisputeModal(false); setDisputeSubmitted(false); }} className="mt-4 px-4 py-2 text-xs rounded-lg" style={{ background: 'var(--bg-input)', color: 'var(--text-secondary)', border: '1px solid var(--border-primary)' }}>Sluiten</button>
              </div>
            ) : (
              <div className="space-y-3">
                <div>
                  <label style={{ ...labelStyle, display: 'block' }} className="mb-2">Reden</label>
                  <BaseSelect value={disputeReason} onChange={val => setDisputeReason(val)} options={[
                    { value: 'lead_invalid', label: 'Lead ongeldig' },
                    { value: 'duplicate', label: 'Duplicaat' },
                    { value: 'out_of_scope', label: 'Buiten werkgebied' },
                    { value: 'no_answer', label: 'Geen gehoor' },
                    { value: 'wrong_info', label: 'Onjuiste informatie' },
                    { value: 'other', label: 'Anders' },
                  ]} />
                </div>
                <div>
                  <label style={{ ...labelStyle, display: 'block' }} className="mb-2">Toelichting</label>
                  <textarea rows={3} value={disputeDetail} onChange={e => setDisputeDetail(e.target.value)} placeholder="Beschrijf het probleem..." maxLength={500} className="w-full text-sm rounded-lg px-3 py-2 resize-none focus:outline-none" style={{ background: 'var(--bg-input)', border: '1px solid var(--border-primary)', color: 'var(--text-secondary)' }} />
                </div>
                <div className="flex justify-end gap-2 mt-5">
                  <button onClick={() => setShowDisputeModal(false)} className="px-3 py-1.5 text-xs rounded-lg border" style={{ color: 'var(--text-muted)', borderColor: 'var(--border-primary)' }}>Annuleren</button>
                  <button disabled={!disputeDetail.trim() || disputeSubmitting} onClick={async () => { setDisputeSubmitting(true); await new Promise(r => setTimeout(r, 800)); setDisputeSubmitting(false); setDisputeSubmitted(true); }} className="px-3 py-1.5 text-xs rounded-lg border disabled:opacity-40" style={{ background: 'var(--danger-bg)', color: 'var(--danger-text)', borderColor: 'var(--danger-border)' }}>
                    {disputeSubmitting ? 'Bezig...' : 'Melding indienen'}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Agenda modal ── */}
      {showAgendaModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.50)', backdropFilter: 'blur(8px)' }}>
          <div className="rounded-xl p-6 w-full max-w-sm" style={{ background: 'var(--bg-modal)', border: '1px solid var(--border-modal)', boxShadow: 'var(--shadow-card)' }}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>Afspraak toevoegen</h3>
              <button onClick={() => setShowAgendaModal(false)} style={{ color: 'var(--text-muted)' }}><X size={16} /></button>
            </div>
            <div className="space-y-3">
              {[
                { label: 'Klantnaam', key: 'name' as const, type: 'text', placeholder: 'Naam klant' },
                { label: 'Datum', key: 'date' as const, type: 'date', placeholder: '' },
                { label: 'Tijd', key: 'time' as const, type: 'time', placeholder: '' },
              ].map(({ label, key, type, placeholder }) => (
                <div key={key}>
                  <label style={{ ...labelStyle, display: 'block' }} className="mb-1">{label}</label>
                  <input type={type} value={agendaForm[key]} onChange={e => setAgendaForm(f => ({ ...f, [key]: e.target.value }))} placeholder={placeholder} className="w-full px-3 py-2 text-sm rounded-lg outline-none" style={inputStyle} />
                </div>
              ))}
              <div>
                <label style={{ ...labelStyle, display: 'block' }} className="mb-1">Type</label>
                <select value={agendaForm.type} onChange={e => setAgendaForm(f => ({ ...f, type: e.target.value }))} className="w-full px-3 py-2 text-sm rounded-lg outline-none" style={inputStyle}>
                  <option value="showroom">Showroom bezoek</option>
                  <option value="werkbezoek">Werkbezoek</option>
                  <option value="installatie">Installatie</option>
                  <option value="anders">Anders</option>
                </select>
              </div>
              <div>
                <label style={{ ...labelStyle, display: 'block' }} className="mb-1">Notities</label>
                <textarea rows={2} value={agendaForm.notes} onChange={e => setAgendaForm(f => ({ ...f, notes: e.target.value }))} className="w-full px-3 py-2 text-xs rounded-lg outline-none resize-none" style={inputStyle} />
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-5">
              <button onClick={() => setShowAgendaModal(false)} className="px-3 py-1.5 text-xs rounded-lg border" style={{ color: 'var(--text-muted)', borderColor: 'var(--border-primary)' }}>Annuleren</button>
              <button onClick={async () => {
                if (!effectivePartnerId || !agendaForm.name || !agendaForm.date) return;
                await addAgendaItem({ ...agendaForm, partner_id: effectivePartnerId, lead_id: lead.id });
                setShowAgendaModal(false);
              }} className="px-3 py-1.5 text-xs rounded-lg" style={{ background: 'var(--accent)', color: '#fff' }}>
                Toevoegen
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Email confirm modal ── */}
      {showEmailPreview && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.65)', backdropFilter: 'blur(12px)' }}>
          <div className="rounded-xl w-full max-w-md overflow-hidden" style={{ background: 'var(--bg-modal)', border: '1px solid var(--border-modal)', boxShadow: 'var(--shadow-card)' }}>
            <div className="flex items-center justify-between px-6 py-4" style={{ borderBottom: '1px solid var(--border-separator)' }}>
              <h3 className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>Offerte versturen</h3>
              <button onClick={() => setShowEmailPreview(false)} style={{ color: 'var(--text-muted)' }}><X size={16} /></button>
            </div>
            <div className="px-6 py-4 space-y-3">
              <div className="text-xs space-y-1.5 p-3 rounded-lg" style={{ background: 'var(--bg-input)', border: '1px solid var(--border-separator)', color: 'var(--text-muted)' }}>
                <p><strong style={{ color: 'var(--text-secondary)' }}>Van:</strong> {companyName}</p>
                <p><strong style={{ color: 'var(--text-secondary)' }}>Aan:</strong> {lead.email} ({lead.firstname} {lead.lastname})</p>
                <p><strong style={{ color: 'var(--text-secondary)' }}>Onderwerp:</strong> Uw offerte op maat · {companyName}</p>
                {selectedBrochures.length > 0 && <p><strong style={{ color: 'var(--text-secondary)' }}>Brochures:</strong> {brochureList}</p>}
              </div>
              <div className="space-y-1.5 text-xs" style={{ color: 'var(--text-muted)' }}>
                {allInLine > 0 && <p>• {selectedProduct?.name} · {surfaceM2} m² × €{pricePerM2}/m² = <strong style={{ color: 'var(--text-primary)' }}>€{allInLine.toFixed(2)}</strong></p>}
                {extrasLines.map(l => <p key={l.name}>• {l.name}: <strong style={{ color: 'var(--text-primary)' }}>€{l.amount.toFixed(2)}</strong></p>)}
                {subtotal > 0 && <p className="pt-1" style={{ borderTop: '1px solid var(--border-separator)' }}>Totaal incl. BTW: <strong style={{ color: 'var(--text-primary)', fontSize: 13 }}>€{total.toFixed(2)}</strong></p>}
              </div>
              {quoteNote && (
                <p className="text-xs italic px-3 py-2 rounded-lg" style={{ background: 'var(--bg-surface-raised)', color: 'var(--text-secondary)', borderLeft: '2px solid var(--border-primary)' }}>
                  "{quoteNote}"
                </p>
              )}
            </div>
            <div className="flex gap-2 px-6 py-4" style={{ borderTop: '1px solid var(--border-separator)' }}>
              <button onClick={handleSendEmail} disabled={quoteSending} className="flex-1 flex items-center justify-center gap-2 py-2.5 text-sm font-medium rounded-lg transition-all disabled:opacity-60" style={{ background: 'var(--accent)', color: '#fff' }}>
                {quoteSending ? 'Versturen...' : <><Send size={14} /> Versturen</>}
              </button>
              <button onClick={() => setShowEmailPreview(false)} className="px-4 py-2.5 text-sm rounded-lg" style={{ background: 'var(--bg-input)', color: 'var(--text-secondary)', border: '1px solid var(--border-primary)' }}>Annuleren</button>
            </div>
          </div>
        </div>
      )}

      {/* URL share modals */}
      {showSigningModal && <UrlShareModal type="signing" url={dynamicSigningUrl || signingUrl} onClose={() => { setShowSigningModal(false); setDynamicSigningUrl(null); }} />}
      {showDeliveryModal && <UrlShareModal type="delivery" url={deliveryUrl} onClose={() => setShowDeliveryModal(false)} />}
      {showPaymentModal && <UrlShareModal type="payment" url={paymentUrl} onClose={() => setShowPaymentModal(false)} />}

    </div>
  );
}
